/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import axios from "axios";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { History, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuditLog() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/owner/audit-log`, { withCredentials: true });
        setItems(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = items.filter((e) => {
    const q = filter.toLowerCase();
    return !q || e.actor_email?.toLowerCase().includes(q) || e.action?.toLowerCase().includes(q) || e.ip?.includes(q);
  });

  return (
    <DashboardShell allowedRoles={["owner"]}>
      <header className="mb-8">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
          <History size={14} /> {t.app.audit.overline}
        </div>
        <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">{t.app.audit.title}</h1>
        <p className="mt-1 text-[#5C6B5C]">{t.app.audit.subtitle}</p>
      </header>

      <div className="relative mb-5 max-w-md">
        <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B5C]" />
        <Input
          data-testid="audit-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t.app.audit.filter_placeholder}
          className="ps-10 h-11 bg-white border-[#E2E4DF] rounded-xl"
        />
      </div>

      <div className="rounded-2xl border border-[#E2E4DF] bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#5C6B5C]">{t.app.common.loading}</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#5C6B5C]">{t.app.audit.empty}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-[#F0F0EB] text-[#5C6B5C] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.audit.th_date}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.audit.th_actor}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.audit.th_action}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.audit.th_target}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.audit.th_ip}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id} data-testid={`audit-row-${e.id}`} className="border-t border-[#E2E4DF]">
                    <td className="px-5 py-3 text-[#5C6B5C] whitespace-nowrap">{new Date(e.ts).toLocaleString()}</td>
                    <td className="px-5 py-3 text-[#1C3A27] font-medium">
                      {e.actor_email}
                      <div className="text-xs text-[#5C6B5C]">{t.app.role[e.actor_role] || e.actor_role}</div>
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-[#1C3A27] text-white">
                        {t.app.audit.actions[e.action] || e.action}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[#5C6B5C] font-mono text-xs">{e.target_id ? e.target_id.slice(0, 8) + "…" : "—"}</td>
                    <td className="px-5 py-3 text-[#5C6B5C] font-mono text-xs">{e.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
